import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";

const TYPES = ["residential", "building", "land", "hotel", "warehouse", "office"];

export default function AIWorkerControls({ onStatus, onFinished }) {
  const { t, i18n } = useTranslation();
  const [city, setCity] = useState("");
  const [propertyType, setPropertyType] = useState("residential");
  const [status, setStatus] = useState(null);
  const [runs, setRuns] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    let timer;
    async function refresh() {
      try {
        const [s, r] = await Promise.all([
          api.get("/ai-worker/status"),
          api.get("/ai-worker/runs"),
        ]);
        if (!active) return;
        setStatus(s.data);
        setRuns(r.data.items || []);
        onStatus(s.data);
      } catch {
        if (active) setError(t("workerControls.loadError"));
      } finally {
        if (active) timer = setTimeout(refresh, 3000);
      }
    }
    refresh();
    return () => { active = false; clearTimeout(timer); };
  }, [t, onStatus]);

  const state = status?.execution_status || status?.status;
  const activeRun = state === "running" || state === "stopping";
  const label = (value) => t(`workerControls.states.${value}`, {
    defaultValue: t("workerControls.unknown"),
  });

  async function run(event) {
    event.preventDefault();
    setError(""); setMessage("");
    if (!city.trim()) {
      setError(t("workerControls.cityRequired"));
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/ai-worker/run", {
        city: city.trim(), property_type: propertyType,
      }, { timeout: 150000 });
    } catch (err) {
      const key = {
        409: "busy", 503: "unavailable", 504: "timeout", 422: "cityRequired",
      }[err.response?.status] || "runError";
      setError(t(`workerControls.${key}`));
    } finally {
      setSubmitting(false);
      onFinished();
    }
  }

  async function stop() {
    setStopping(true); setError(""); setMessage("");
    try {
      const response = await api.post("/ai-worker/stop");
      if (response.data.stop_requested) {
        setMessage(t("workerControls.stopRequested"));
        setStatus((value) => ({ ...value, execution_status: "stopping" }));
      }
    } catch {
      setError(t("workerControls.stopError"));
    } finally {
      setStopping(false);
    }
  }

  return (
    <div className="bg-white border border-slate-200 rounded p-5 space-y-4">
      <h2 className="font-semibold">{t("workerControls.title")}</h2>
      <p className="text-sm text-slate-600">{t("workerControls.notice")}</p>
      <form onSubmit={run} className="flex flex-wrap items-end gap-3">
        <label className="space-y-1">
          <span className="block text-sm">{t("workerControls.city")}</span>
          <input value={city} onChange={(e) => setCity(e.target.value)}
            maxLength={120} required disabled={submitting || activeRun}
            className="border rounded px-3 py-2" />
        </label>
        <label className="space-y-1">
          <span className="block text-sm">{t("workerControls.type")}</span>
          <select value={propertyType} onChange={(e) => setPropertyType(e.target.value)}
            disabled={submitting || activeRun} className="border rounded px-3 py-2">
            {TYPES.map((value) => (
              <option key={value} value={value}>{t(`workerControls.types.${value}`)}</option>
            ))}
          </select>
        </label>
        <button type="submit"
          disabled={!status?.search_enabled || submitting || activeRun}
          className="bg-[#0B2545] text-white rounded px-4 py-2 disabled:opacity-50">
          {t("workerControls.run")}
        </button>
        <button type="button" onClick={stop}
          disabled={state !== "running" || stopping}
          className="border border-[#C5A059] bg-[#C5A059] text-[#0B2545] hover:bg-[#B8924E] rounded px-4 py-2 disabled:opacity-50">
          {t("workerControls.stop")}
        </button>
      </form>
      {message && <p role="status">{message}</p>}
      {error && <p role="alert" className="text-red-700">{error}</p>}
      <h3 className="font-semibold">{t("workerControls.history")}</h3>
      {runs.length === 0 ? <p>{t("workerControls.empty")}</p> : (
        <ul className="divide-y divide-slate-200">
          {runs.map((item) => (
            <li key={item.id} className="py-3 space-y-1">
              <p className="font-medium">{item.city} · {label(item.state)}</p>
              <p className="text-sm">
                {new Date(item.started_at).toLocaleString(
                  i18n.language === "zh" ? "zh-CN" : i18n.language
                )}
              </p>
              <p className="text-sm text-slate-600">
                {t("workerControls.found")}: {item.found ?? "—"} ·{" "}
                {t("workerControls.saved")}: {item.saved ?? "—"} ·{" "}
                {t("workerControls.duplicates")}: {item.duplicates ?? "—"}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
